"use strict";

import Analytics from "/js/util/analytics.js";
import Util from "/js/util/util.js";
import AppUtil from "/js/util/app-util.js";
import FadeUtil from "/js/util/fade-util.js";
import Global from "/js/core/global.js";
import PrimerMaster from "/js/util/primermaster.js";
import DragDelegate from "/js/util/drag-delegate.js";
import MinicourseListItemView from "/js/top-level-views/minicourse-list-item-view.js"
import UserLessons from "/js/core/user-lessons";
import Strings from "/js/util/strings.js";
import Constants from "/js/core/constants.js";
import FocusUtil from '/js/util/focus-util';

/** 
 * View showing list of lessons in the minicourse.
 * Is displayed either full-size or as a sidebar.
 *
 * Has elaborate sidebar opening-and-closing logic. Note how, when in closed state,
 * the sidebar contents must be display:none  or else will be reachable via accessibility controls.
 */
export default class MinicourseView {

  #$el = $("#minicourseView");

  // Holder of the content, is either full browser size, or sidebar-sized.
  #$content = this.#$el.find("#minicourseContent");
  // Inner holder of content, is scrollable
  #$inner = this.#$el.find('#minicourseInner');
  // Holder of the list items, below the description text.
  #$listHolder = this.#$el.find("#minicourseListHolder");
  // Covers entire browser area, semi-opaque.
  #$screen = this.#$el.find("#minicourseScreen");
  #$dragHit = this.#$el.find("#minicourseDragHit");
  #$tab = this.#$el.find("#minicourseTab");
  #$tabStrip = this.#$el.find("#minicourseTabStrip");

  #items = [];
  #dragDelegate;
  #leftStartDrag;
  #isFirstSidebarOpen = true;
  #closeSidebarTimeoutId; // ugh

  constructor(data=null) {

    // Handlers
    $(window).on("minicourse-list-item", (e, ...rest) => this.#onItem(e, ...rest));

    this.#$screen.on("click tap", e => this.#onScreenClick(e));

    this.#$tab.hover(e => this.#$tab.addClass("runAnim"));
    FocusUtil.setEnterHandler(this.#$tab, e => this.#onTabEnter(e));
    this.#$tab.on("click tap", e => FocusUtil.blurActiveElement());

    this.#dragDelegate = new DragDelegate(this, this.#$dragHit);
    this.#dragDelegate.setCallbacks(
        this.#onDragHitDelegateDown, this.#onDragHitDelegateMove, 
        this.#onDragHitDelegateUp, this.#onDragHitDelegateClick);

    if (data) {
      this.populate(data);
    }
  }

  get $el() {
    return this.#$el;
  }

  get isOpen() {
    return this.#$el.hasClass("isOpen");
  }

  /**
   * Note it is App (and not this class) that is responsible for opening and closing the sidebar.
   */
  openSidebar() {
    this.#resetSidebarState();
    this.update();
    this.#$el.addClass("isOpen");
    $('#minicourseContent').css('left', '');
    this.#showInner();

    if (this.#isFirstSidebarOpen) {
      this.#isFirstSidebarOpen = false;
      this.#$el.find("#minicourseInner").scrollTop(0);
    }
  }

  closeSidebar() {
    this.#resetSidebarState();
    this.update();
    this.#$el.addClass("isClosed");
    this.#updateContentLeft(false);  // special case

    // Wait for sidebar to animate-out before hiding its contents:
    clearTimeout(this.#closeSidebarTimeoutId);
    this.#closeSidebarTimeoutId = setTimeout(() => {
      this.#hideInner();
    }, Constants.generalAnimDuration);
  }

  /**
   * Shows view in its full-sized form.
   */
  show() {
    this.update();
    this.#$el.show(); // TODO: fix this.
    this.#showInner();
    FadeUtil.fadeIn(this.#$el);
    Util.windowUnselect();
  }

  /**
   * Shows view in its _closed_ sidebar state
   */
  showAsClosedSidebar() {
    const isAlready = this.isSidebar;

    this.#$el.addClass("isSidebar isClosed");
    this.#$el.css('display', 'block');
    this.#updateContentLeft(true);

    // Also "animate in" drag-hit-area:
    if (!isAlready) {
      AppUtil.setCssWithoutTransition(this.#$dragHit, () => { this.#$dragHit.css("right", "0") });
      this.#$dragHit.css("right", "");  // ie, default position
    }

    this.show();

    this.#hideInner();

    Util.windowUnselect();
  }

  populate(minicourseData) {
    const $title = this.#$el.find("#minicourseTitle");
    $title.text(minicourseData.title || "");

    const $copy = this.#$el.find("#minicourseCopy");
    $copy.text(minicourseData.copy || "");

    const $credit = this.#$el.find("#minicourseCredit");
    const creditString = Strings.get("lesson_credit");
    if (minicourseData.credit) {
      const localizedCredit = creditString.replace("%1$s", minicourseData.credit);
      $credit.text(localizedCredit);
    }

    this.#$listHolder.empty();
    minicourseData.lessonIds.forEach((id) => {
      const o = PrimerMaster.getLessonById(id);
      if (!o) {
        cl(`WARNING: Unrecognized lesson id in minicourse data ${id}. Ignoring.`);
        return;
      }
      const item = new MinicourseListItemView(o);
      this.#items.push(item);
      this.#$listHolder.append(item.$el);
    });

    if (this.#items.length % 2 === 1) {
      // Add dummy item at the end.
      const $dummy = $(`<div class="minicourseDummyListItem"></div>`);
      this.#$listHolder.append($dummy);
    }

    // Complete state - playstore/appstore hrefs and image paths
    const $playStoreAnchor = this.#$el.find("a.playstore");
    $playStoreAnchor.attr("href", Strings.get("playstore_app_url"));
    const $playStoreImg = $playStoreAnchor.find("img");
    $playStoreImg.attr("src", "img/" + Strings.get("playstore_badge_filename"));

    const $appStoreAnchor = this.#$el.find("a.appstore");
    $appStoreAnchor.attr("href", Strings.get("appstore_app_url"));
    const $appStoreImg = $appStoreAnchor.find("img");
    $appStoreImg.attr("src", "img/" + Strings.get("appstore_badge_filename"));

    $playStoreAnchor.on("click tap", e => FocusUtil.blurActiveElement());
    $appStoreAnchor.on("click tap", e => FocusUtil.blurActiveElement());
  }

  hide() {
    FadeUtil.fadeOut(this.#$el);
  }

  selectItem(lessonId) {
    this.#items.forEach(item => {
      (lessonId === item.lessonId)
          ? item.$el.addClass("selected")
          : item.$el.removeClass("selected");
    });
  }

  update() {
    // Update items' state
    this.#items.forEach(item => { item.update(); });
    // Update selected item
    this.selectItem(Global.lessonId);
    // Update overall complete state
    let isComplete = true;
    for (const item of this.#items) {
      if (!UserLessons.isComplete(item.lessonId)) {
        isComplete = false;
        break;
      }
    }
    if (isComplete) {
      this.#$el.addClass("isMinicourseComplete");
      setTimeout(() => this.#$inner.scrollTop(0), 1);
    } else {
      this.#$el.removeClass("isMinicourseComplete");
    }
  }

  get isSidebar() {
    return this.$el.hasClass("isSidebar");
  }

  get isClosed() {
    return this.isSidebar && this.$el.hasClass("isClosed");
  }

  resize() {
    this.#updateContentLeft();
  }

  /**
   * Sets #minicourseContent's `left` value to be the negative of its width
   * when in its closed state.
   *
   * Doing this with css is not working correctly, not sure why.
   * This complicates this class a lot.
   */
  #updateContentLeft(now=true) {

    let leftValue;
    if (this.isClosed) {
      leftValue = this.#$content.width() * -1;
    } else {
      leftValue = "";
    }
    const f = () => this.#$content.css("left", leftValue + "px");
    if (now) {
      AppUtil.setCssWithoutTransition(this.#$content, f);
    } else {
      f();
    }
  }

  #getSidebarOpenPercentage() {
    // is 100% open when "left" is at 0
    const left = parseFloat(this.#$content.css("left"));
    const result = 1 - (left / -this.#$content.width());
    return result;
  }

  #resetSidebarState() {
    // Remove main sidebar qualifiers
    this.#$el.removeClass("isClosed");
    this.#$el.removeClass("isOpen");
    // Remove any no-transition classes that may have been applied
    this.#$content.removeClass("noTransition");
    this.#$el.removeClass("noTransition");
    this.#$tabStrip.removeClass("noTransition");
    // Clear any programmatically applied css values
    this.#$el.css("background-color", "");
    this.#$tabStrip.css("opacity", "");
    // ... special case
    this.#updateContentLeft()
  }

  #doTabClick() {
    if (this.#getSidebarOpenPercentage() < 0.5) {
      this.#$el.trigger("minicourse-sidebar-open");
      Analytics.trackSideBarButtonOpen("click");
    } else {
      this.#$el.trigger("minicourse-sidebar-close");
      Analytics.trackSideBarButtonClose("click");
    }
  }

  #showInner() {
    this.#$inner.css("display", "block");
  }

  #hideInner() {
    clearTimeout(this.#closeSidebarTimeoutId);
    this.#$inner.css("display", "none");
  }

  #onItem(e, lessonId) {
    let progress = "not_started";
    const userLesson = UserLessons.getByLessonId(lessonId);
    if (userLesson) {
      if (userLesson.isComplete) {
        progress = "completed";
      } else {
        progress = "in_progress";
      }
    }
    Analytics.lessonTrackingSource = this.isSidebar ? "sidebar" : "lesson_set";
    Analytics.trackLessonListItemSelected(lessonId, progress);
    const setStarted = UserLessons.saveStartedSet(Global.minicourseData.id);
    if (setStarted) {
      Analytics.trackSetStart(lessonId);
    }

    this.selectItem(lessonId);
    $(window).trigger('minicourse-item-select', lessonId);
  }

  #onScreenClick(e) {
    if (!this.isSidebar) {
      return;
    }
    Analytics.trackSideBarButtonClose("screen");
    this.#$el.trigger("minicourse-sidebar-close");
  }

  #onDragHitDelegateDown(px, py) {
    this.#leftStartDrag = parseFloat( this.#$content.css("left") );
    this.#showInner();
    // Ensure css changes are synchronous during gesture:
    this.#$el.addClass("noTransition");
    this.#$content.addClass("noTransition");
    this.#$tabStrip.addClass("noTransition");

    if (this.#isFirstSidebarOpen) {
      this.#isFirstSidebarOpen = false;
      this.#$el.find("#minicourseInner").scrollTop(0);
    }
  }

  #onDragHitDelegateMove(dx, dy, b) {
    if (this.#dragDelegate.isStillClick) {
      return;
    }
    // Move sidebar
    let dest = this.#leftStartDrag + dx;
    dest = Math.min(dest, 0);
    dest = Math.max(dest, -this.#$content.width());

    this.#$content.css("left", dest);
    // Scrim opacity
    let pct = (dest - -this.#$content.width()) / this.#$content.width();
    this.#$el.css("background-color", "rgba(0,0,0," + (pct * 0.5) +")"); // must match css
    // Thin strip opacity
    pct = 1 - pct;
    pct = Math.pow(pct, 5);  // accel curve har
    this.#$tabStrip.css("opacity", pct);
  }

  #onDragHitDelegateUp(x, y, dx, dy) {
    // Snap sidebar:
    if (this.#getSidebarOpenPercentage() > 0.5) {
      this.#$el.trigger("minicourse-sidebar-open");
      Analytics.trackSideBarButtonOpen("drag");
    } else {
      this.#$el.trigger("minicourse-sidebar-close");
      Analytics.trackSideBarButtonClose("drag");
    }
    FocusUtil.blurActiveElement();
  }

  #onDragHitDelegateClick(x, y, dx, dy, e) {
    if (e.target !== this.#$tab[0]) {
      return;
    }
    FocusUtil.blurActiveElement();
    this.#doTabClick();
  }

  #onTabEnter(e) {
    const pct = this.#getSidebarOpenPercentage();
    if (pct !== 0 && pct !== 1) {
      return;
    }
    this.#doTabClick();
  }
}
